import type React from "react";
import { useMemo, useState } from "react";

import debounce from "lodash/debounce";

import { colorsPalettes } from "@similarweb/styles";
import { Dropdown, DropdownButton } from "@similarweb/ui-components";

import { translate } from "app/filters/i18nFilter";
import AIAgentTrackingService from "app/pages/sales-intelligence/modules/ai-agent/services/tracking/trackingService";
import { StyledDropdownItem } from "app/pages/sales-intelligence/modules/ai-agent/sub-modules/agent-knowledge-base/components/LanguageDropdown/styles";
import { StyledDropdownContainer } from "app/pages/sales-intelligence/modules/ai-agent/sub-modules/agent-knowledge-base/components/RolesDropdown/styles";
import { WithTitleContainer } from "app/pages/sales-intelligence/modules/ai-agent/sub-modules/agent-knowledge-base/components/WithTitleContainer/WithTitleContainer";
import { ROLES_OTHER_OPTION } from "app/pages/sales-intelligence/modules/ai-agent/sub-modules/agent-knowledge-base/hooks/useKnowledgeBase";
import { TextField } from "app/pages/sales-intelligence/pages/ai-agent/components/TextfField/TextField";
import { StyledBaseDropdownButtonWrap } from "app/pages/sales-intelligence/sub-modules/advanced-search/components/common-styles";

const DROPDOWN_ID = "si-agent-knowledge-base-roles-dd";
const DROPDOWN_POPUP_HEIGHT = 44 * 4;

type Props = {
    value: string | undefined;
    optionValue: string | undefined;
    onChange: (value: string) => void;
    onOptionChange: (value: string) => void;
    rolesOptions: string[];
    dataAutomation?: string;
};

export const RolesDropdown: React.FC<Props> = ({
    value,
    onChange,
    rolesOptions,
    onOptionChange,
    optionValue,
    dataAutomation,
}) => {
    const [isTextFieldAutoFocus, setIsTextFieldAutoFocus] = useState(false);
    const selectedIds = optionValue ? { [optionValue]: true } : {};
    const selectedOptionText = useMemo(
        () => rolesOptions?.find((option) => option === optionValue) ?? "",
        [optionValue, rolesOptions],
    );

    const handleSelect = (option: { id: string }) => {
        if (option.id === ROLES_OTHER_OPTION) {
            onChange("");
            setIsTextFieldAutoFocus(true);
        } else {
            AIAgentTrackingService.kb.trackRoleSelect(option.id);
            onChange(option.id);
        }
        onOptionChange(option.id);
    };

    const handleTextFieldChange = (value: string) => {
        onChange(value);
        debounce(() => {
            AIAgentTrackingService.kb.trackRoleSelect(value);
        }, 300)();
    };

    const renderDDButton = () => {
        return (
            <StyledBaseDropdownButtonWrap buttonTextColor={colorsPalettes.carbon[400]}>
                <DropdownButton>{selectedOptionText}</DropdownButton>
            </StyledBaseDropdownButtonWrap>
        );
    };

    const renderOptions = () => {
        return (
            rolesOptions?.map((option) => (
                <StyledDropdownItem
                    key={option}
                    id={option}
                    text={option}
                >
                    {option}
                </StyledDropdownItem>
            )) ?? []
        );
    };

    return (
        <WithTitleContainer
            title={translate("si.agent_knowledge_base.modal.roles.title")}
            dataAutomation={dataAutomation}
        >
            <StyledDropdownContainer id={DROPDOWN_ID}>
                <Dropdown
                    appendTo={`#${DROPDOWN_ID}`}
                    selectedIds={selectedIds}
                    onClick={handleSelect}
                    virtualize={false}
                    searchPlaceHolder={translate("si.agent_knowledge_base.modal.roles.search")}
                    dropdownPopupHeight={DROPDOWN_POPUP_HEIGHT}
                    dropdownPopupPlacement="bottom-left"
                    autoPlacement={false}
                    hasSearch
                >
                    {[renderDDButton(), ...renderOptions()]}
                </Dropdown>
                {optionValue === ROLES_OTHER_OPTION ? (
                    <TextField
                        placeholder={translate("si.agent_knowledge_base.modal.roles.textfield.placeholder")}
                        onChange={handleTextFieldChange}
                        margin="8px 0 0"
                        defaultValue={value}
                        autoFocus={isTextFieldAutoFocus}
                        width={502}
                    />
                ) : null}
            </StyledDropdownContainer>
        </WithTitleContainer>
    );
};
